import { useState, type FormEvent, type ReactNode } from "react"
import { Loader2, CheckCircle } from "lucide-react"
import { cn } from "@/lib/utils"
import { AuthField } from "../AuthField/AuthField"
import { Input } from "../Input/Input"
import { Button } from "../Button/Button"

export interface ForgotPasswordFormProps {
  heading?: string
  description?: string
  sentHeading?: string
  sentMessage?: ReactNode
  sent?: boolean
  defaultSent?: boolean
  sentEmail?: string
  defaultEmail?: string
  loading?: boolean
  error?: string
  emailLabel?: string
  submitLabel?: string
  submittingLabel?: string
  backLinkSlot?: ReactNode
  sentBackLinkSlot?: ReactNode
  onSubmit?: (email: string) => void
  className?: string
}

export function ForgotPasswordForm({
  heading = "Reset your password",
  description = "Enter your email and we'll send a reset link.",
  sentHeading = "Check your email",
  sentMessage,
  sent,
  defaultSent = false,
  sentEmail,
  defaultEmail = "",
  loading = false,
  error,
  emailLabel = "Email",
  submitLabel = "Send Reset Link",
  submittingLabel = "Sending...",
  backLinkSlot,
  sentBackLinkSlot,
  onSubmit,
  className,
}: ForgotPasswordFormProps) {
  const [email, setEmail] = useState(defaultEmail)
  const [internalSent, setInternalSent] = useState(defaultSent)
  const isControlledSent = sent !== undefined
  const isSent = isControlledSent ? sent : internalSent

  const handleSubmit = (ev: FormEvent<HTMLFormElement>) => {
    ev.preventDefault()
    onSubmit?.(email)
    if (!isControlledSent) setInternalSent(true)
  }

  const resolvedSentEmail = sentEmail ?? email

  if (isSent) {
    const message = sentMessage ?? (
      <>
        Password reset link sent to{" "}
        <strong className="text-foreground">{resolvedSentEmail}</strong>. Check your inbox.
      </>
    )
    return (
      <div className={cn("text-center py-4", className)}>
        <CheckCircle size={36} strokeWidth={1.2} className="text-primary mx-auto mb-3" />
        <h1 className="text-page-title text-foreground mb-1.5">{sentHeading}</h1>
        <p className="text-body text-muted-foreground leading-relaxed max-w-[280px] mx-auto mb-4">
          {message}
        </p>
        {sentBackLinkSlot}
      </div>
    )
  }

  return (
    <div className={cn("flex flex-col", className)}>
      <div className="text-center mb-5">
        <h1 className="text-page-title text-foreground mb-1">{heading}</h1>
        <p className="text-body text-muted-foreground">{description}</p>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-3.5">
        <AuthField label={emailLabel} error={error}>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
            className="h-9 text-body bg-background border-border"
          />
        </AuthField>

        <Button type="submit" disabled={loading} className="w-full text-label">
          {loading && <Loader2 size={14} className="animate-spin mr-1.5" />}
          {loading ? submittingLabel : submitLabel}
        </Button>
      </form>

      {backLinkSlot && (
        <p className="text-center text-body text-muted-foreground mt-5">{backLinkSlot}</p>
      )}
    </div>
  )
}
